import * as React from 'react'

import { F0Button } from '@factorialco/f0-react'
import {
  AddEmployeesToAttendancePolicyDocument,
  AttendancePoliciesSelectDocument,
  ProfilePageDocument,
} from 'generated/resources/graphql'

import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import SelectField from 'modules/core/components/SelectField'
import asyncAction from 'modules/core/lib/asyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  employeeId: string
  attendanceEmployeesPolicyId: string | null
}

const TimeTrackingForm = ({ employeeId, attendanceEmployeesPolicyId }: Props) => {
  const [selectedPolicyId, setSelectedPolicyId] = React.useState<string | null>(
    attendanceEmployeesPolicyId
  )
  const [savedPolicyId, setSavedPolicyId] = React.useState<string | null>(
    attendanceEmployeesPolicyId
  )

  React.useEffect(() => {
    setSelectedPolicyId(attendanceEmployeesPolicyId)
    setSavedPolicyId(attendanceEmployeesPolicyId)
  }, [attendanceEmployeesPolicyId])

  const isDirty = selectedPolicyId !== savedPolicyId

  const { data } = useQuery(AttendancePoliciesSelectDocument, { variables: {} })

  const [addEmployeesToAttendancePolicy, { loading }] = useMutation(
    AddEmployeesToAttendancePolicyDocument,
    {
      extractorFn: (data) => data.attendanceMutations.addEmployeesAttendanceEmployeesPolicy,
      refetchQueries: [ProfilePageDocument],
    }
  )

  const options =
    data?.attendance?.employeesPoliciesConnection?.edges
      .filter(({ node }) => node.id && node.name)
      .map(({ node }) => ({
        label: node.name,
        value: node.id,
      })) ?? []

  async function onSubmit() {
    if (!selectedPolicyId) return

    const result = await asyncAction(
      () =>
        addEmployeesToAttendancePolicy({
          variables: {
            policyId: selectedPolicyId,
            employeeIds: [employeeId],
          },
        }),
      {
        successMessage: i18n.t('time_tracking.employee_profile.update_success'),
      }
    )

    if (result?.success) {
      setSavedPolicyId(selectedPolicyId)
    }
  }

  return (
    <Form onSubmit={onSubmit}>
      <FormRow>
        <SelectField
          label={i18n.t('time_tracking.employee_profile.title')}
          value={selectedPolicyId ?? undefined}
          options={options}
          onChange={(value) => {
            setSelectedPolicyId(value)
          }}
        />
      </FormRow>
      {isDirty && (
        <FormRow footer>
          <F0Button type="submit" label={i18n.t('common.submit')} loading={loading} />
        </FormRow>
      )}
    </Form>
  )
}

export default TimeTrackingForm
